import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Label,
  Media,
  Row,
} from "reactstrap";
import { findOneSurvey, makeVote } from "../../helpers/api/survey";
import { SUCCESS_RESPONSE } from "../../helpers/codeResponses/codes";
import handleErrorMessage, {
  getErrorMessage,
} from "../../helpers/errors/handleErrorMessage";
import withRedirect from "../../helpers/hoc/withRedirect";
import Flex from "../common/Flex";
import PageHeader from "../common/PageHeader";
import ComposeAttachment from "../complain/ComposeAttachment";
import SupportLink from "../root/support/components/SupportLink";
import { PreloadState } from "../UI/preloading/PreloadState";
import { Toast } from "../UI/toast/Toast";
import moment from "moment";

const Voting = ({ setRedirect, setRedirectUrl }) => {
  const history = useHistory();

  const { voting } = useParams();

  const { role } = useSelector((state) => state.auth.user);

  const [survey, setSurvey] = useState({
    data: null,
    isLoading: true,
    error: null,
  });
  const [attachments, setAttachments] = useState([]);
  const [optionSelected, setOptionSelected] = useState(null);
  const [isDisabled, setIsDisabled] = useState(true);

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (voting) {
      const getDetails = () => {
        findOneSurvey(voting)
          .then(({ data }) => setSurvey((prev) => ({ ...prev, data: data })))
          .catch((error) => {
            handleErrorMessage(error);
            setSurvey((prev) => ({ ...prev, error: getErrorMessage(error) }));
          })
          .finally(() => setSurvey((prev) => ({ ...prev, isLoading: false })));
      };
      getDetails();
    }
  }, [voting]);

  useEffect(() => {
    if (survey.data) {
      if (survey.data.files.length > 0) {
        for (let i = 0; i < survey.data.files.length; i++) {
          let newFile = {
            id: i,
            name: survey.data.files[i].id + survey.data.files[i].file,
            size: null,
            type: survey.data.files[i].file,
          };
          setAttachments((prev) => [...prev, newFile]);
        }
      }
    }
  }, [survey]);

  useEffect(() => {
    setIsDisabled(!optionSelected);
  }, [optionSelected]);

  const vote = () => {
    setSubmitting(true);
    makeVote(optionSelected)
      .then(({ code }) => {
        if (code === SUCCESS_RESPONSE.OK) {
          Toast.fire({
            type: "success",
            title: `Voto realizado exitosamente.`,
          });
          history.goBack();
        }
      })
      .catch((error) => handleErrorMessage(error))
      .finally(() => setSubmitting(false));
  };

  return (
    <>
      <PageHeader
        title={`Detalles de la ${survey === "1" ? "encuesta" : "votación"}.`}
        description={
          role === "manager"
            ? `Visualiza el progreso de la ${
                survey === "1" ? "encuesta" : "votación"
              }.`
            : "Vota por la opción que prefieras."
        }
        className="mb-3"
        col={{ lg: 9 }}
      >
        {role === "manager" && <SupportLink />}
      </PageHeader>
      <Card>
        <PreloadState
          loading={!!survey.isLoading || submitting}
          message={submitting && "Un momento, porfavor espere..."}
        >
          <CardHeader>
            <Row>
              <Col>
                <Media>
                  <Media body className="ml-2">
                    <h5 className="mb-0">{survey.data?.voting_data.title}</h5>
                  </Media>
                </Media>
              </Col>
              <Col tag={Flex} align="center" xs="auto" className="ml-auto">
                <small>
                  {moment().isAfter(survey.data?.voting_data?.due_date)
                    ? "Expiró "
                    : "Expira "}{" "}
                  {moment(survey.data?.voting_data.due_date).fromNow()}
                </small>
              </Col>
            </Row>
          </CardHeader>
          <CardBody className="bg-light">
            <p className="fs-2 font-weight-bolder">{survey.data?.details}</p>
            <p className="mb-3">Selecciona una opción:</p>
            {survey.data?.options.map(({ option, id }) => (
              <Card
                key={id}
                className={classNames("cursor-pointer mb-3", {
                  "bg-primary":
                    optionSelected === id || survey.data?.voted?.id === id,
                  "text-white":
                    optionSelected === id || survey.data?.voted?.id === id,
                })}
                onClick={() => {
                  if (!survey.data?.voted) {
                    setOptionSelected(id);
                  }
                }}
              >
                <CardBody>
                  <p className="fs-1 font-weight-bold mb-0">• {option}</p>
                </CardBody>
              </Card>
            ))}
            {attachments.length > 0 && (
              <div className="bg-light px-card py-3">
                <Flex column inline>
                  <Label className="ml-2">Archivos adjuntos</Label>
                  {attachments.map((attachment, i) => (
                    <ComposeAttachment
                      {...attachment}
                      key={attachment.id}
                      index={i}
                      downloadPath={attachment.type}
                      download={true}
                    />
                  ))}
                </Flex>
              </div>
            )}
          </CardBody>
          <CardFooter>
            <Flex align="center" justify="end">
              <Button
                color="falcon-default"
                size="sm"
                className="px-5 mr-2"
                onClick={() => history.push("/dashboard/surveys")}
              >
                Volver
              </Button>
              {!survey.data?.voted ? (
                <Button
                  color="primary"
                  size="sm"
                  className="px-5 mr-2"
                  onClick={vote}
                  disabled={isDisabled}
                  type="submit"
                >
                  Votar
                </Button>
              ) : (
                <Button
                  color="primary"
                  size="sm"
                  className="px-5 mr-2"
                  disabled
                  type="submit"
                >
                  Ya votaste <FontAwesomeIcon icon={faCheck} />
                </Button>
              )}
            </Flex>
          </CardFooter>
        </PreloadState>
      </Card>
    </>
  );
};

export default withRedirect(Voting);
